
export class FormTextarea extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'name', 'value', 'placeholder', 'required', 'disabled', 'rows'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            if (name === 'value' && this.shadowRoot) {
                const textarea = this.shadowRoot.querySelector('textarea');
                if (textarea) textarea.value = newValue;
            } else {
                this.render();
            }
        }
    }

    get value() {
        return this.shadowRoot?.querySelector('textarea')?.value || '';
    }

    set value(val: string) {
        this.setAttribute('value', val);
    }

    render() {
        const label = this.getAttribute('label') || '';
        const name = this.getAttribute('name') || '';
        const value = this.getAttribute('value') || '';
        const placeholder = this.getAttribute('placeholder') || '';
        const required = this.hasAttribute('required');
        const disabled = this.hasAttribute('disabled');
        const rows = this.getAttribute('rows') || '3';

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        margin-bottom: 1.5rem;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }
                    label {
                        color: #cbd5e1;
                        font-size: 0.9rem;
                        font-weight: 600;
                    }
                    textarea {
                        width: 100%;
                        padding: 0.75rem;
                        background: rgba(255, 255, 255, 0.05);
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        border-radius: 8px;
                        color: white;
                        font-size: 0.9rem;
                        font-family: inherit;
                        box-sizing: border-box;
                        resize: vertical;
                        min-height: 80px;
                    }
                    textarea:focus {
                        outline: none;
                        border-color: var(--accent, #f59e0b);
                        box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
                    }
                    textarea:disabled {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                </style>
                <div class="form-group">
                    ${label ? `<label for="${name}">${label} ${required ? '*' : ''}</label>` : ''}
                    <textarea 
                        id="${name}" 
                        name="${name}" 
                        placeholder="${placeholder}"
                        rows="${rows}"
                        ${required ? 'required' : ''}
                        ${disabled ? 'disabled' : ''}
                    >${value}</textarea>
                </div>
            `;

            // Re-attach value if re-rendering and it lost state, though value attribute should handle init.
            // But if user typed, attribute might be stale unless synced. 
            // Minimal implementation: rely on internal state if needed, but here simple render is okay.
            const ta = this.shadowRoot.querySelector('textarea');
            if (ta) {
                ta.addEventListener('input', (e) => {
                    this.dispatchEvent(new CustomEvent('input', { bubbles: true, composed: true }));
                });
                ta.addEventListener('change', (e) => {
                    this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true }));
                });
            }
        }
    }
}

customElements.define('form-textarea', FormTextarea);
